/**
 * Branding & Conceptual Package — canonical definition.
 *
 * The package used to be a single toggle whose only explanation was the
 * comma-separated string "Messaging, brand book, fact sheet, location map
 * visual, logo development." — a $10,000 line with no way for a client to
 * see what they were buying. This module is the one place that defines what
 * the package actually contains, in the words the client reads.
 *
 * Three surfaces render from this file:
 *   1. the Collateral Suite service menu   (VexosMarketingCollateral.jsx)
 *   2. the web proposal / present view     (ProposalPage.jsx)
 *   3. the PDF proposal                    (backend/services/branding_package.py
 *                                           — generated from this file; edit here)
 *
 * HOUSE RULE: one short sentence per `blurb`, and nothing else per
 * deliverable — the client is reading a price sheet, not a brochure.
 *
 * EDITING: this is sales copy, not logic. Change the strings freely — the
 * three surfaces pick the change up with no other edits. The Python mirror
 * must be regenerated to match; `backend/tests/test_branding_package.py`
 * fails if the two drift apart.
 */

/** The one-liner used where only a single line of subtext fits (itemized
 *  price rows in the scope list, upsell menus, change-order labels). */
export const BRANDING_SHORT_DESC =
  "A complete identity system — positioning, name and messaging, logo, brand deck, one sheet, and location map — delivered as production-ready files.";

/** The headline promise. Sets up the package before the deliverables. */
export const BRANDING_PROMISE =
  "Everything the project sells under — defined once, documented, and handed to your team as a system.";

export const BRANDING_LEDE =
  "A development gets marketed by many hands: an agency, a signage fabricator, a printer, a broker team. This defines the identity once so every one of them builds the same project.";

/**
 * The five deliverables, grouped into the three stages the engagement runs
 * in. `blurb` is one short sentence — what it is and why it matters. That is
 * the whole entry: an itemized artifact list under each one read as filler
 * beside a title that already names the thing.
 */
export const BRANDING_STAGES = [
  {
    id: "foundation",
    label: "Stage 01 · Foundation",
    caption: "Who it is for, and what it says.",
    deliverables: [
      {
        id: "positioning",
        title: "Brand Discovery & Positioning",
        blurb:
          "A working session and competitive review that settles who the project is for, plus the name, tagline, and messaging it sells with.",
      },
    ],
  },
  {
    id: "identity",
    label: "Stage 02 · Visual identity",
    caption: "The mark, the palette, and the rules.",
    deliverables: [
      {
        id: "logo",
        title: "Logo, Color & Typography",
        blurb:
          "Three logo concepts and two refinement rounds, delivered with the palette and type pairing the whole project runs on.",
      },
      {
        id: "branddeck",
        title: "Project Brand Deck",
        blurb:
          "One deck holding the logo, identity, colors, fonts, and usage rules — the file you hand any vendor who touches the project.",
      },
    ],
  },
  {
    id: "salestools",
    label: "Stage 03 · Sales tools",
    caption: "What the sales team hands a buyer.",
    deliverables: [
      {
        id: "onesheet",
        title: "Project One Sheet",
        blurb:
          "A single page of unit mix, sizes, finishes, amenities, and timeline for brokers to hand buyers at the end of a tour.",
      },
      {
        id: "locationmap",
        title: "Location & Context Map Visual",
        blurb:
          "A custom-illustrated map placing the project against the transit, employment, schools, and dining that make the address valuable.",
      },
    ],
  },
];

/** Flat list, in presentation order — convenient for numbered grids. */
export const BRANDING_DELIVERABLES = BRANDING_STAGES.flatMap((s) => s.deliverables);

/** How the engagement runs. Phases, deliberately NOT dated — a week-by-week
 *  schedule in a proposal is a promise we get held to, and the real pace is
 *  set by how fast the client turns feedback around. */
export const BRANDING_PROCESS = [
  { step: "01", title: "Discovery",  detail: "Kickoff, competitive review, positioning approved." },
  { step: "02", title: "Concept",    detail: "Logo directions and messaging presented." },
  { step: "03", title: "Refinement", detail: "Rounds on the chosen direction." },
  { step: "04", title: "Delivery",   detail: "Brand deck, one sheet, and map delivered." },
];

export const BRANDING_DURATION_NOTE =
  "Timing depends on feedback turnaround. The package runs in parallel with visualization production and does not extend the render schedule.";

/** Scope boundaries. Mirrors the Terms & Conditions on the agreement page —
 *  revisions and ownership are stated there and must not contradict here. */
export const BRANDING_INCLUSIONS = [
  "Two revision rounds on every deliverable, per the Revisions clause of the agreement.",
  "One dedicated project lead and designer throughout.",
  "Ownership of the final deliverables transfers to you on full payment.",
  "Print production, signage fabrication, website build, and media buying are quoted separately.",
];
